import { MenuItem, TextFieldProps } from '@material-ui/core';
import { ChangeEvent, ReactNode, Ref, forwardRef } from 'react';
import { WbTextField, AccessibilityProps } from './WbTextField';
import { ClearButton } from './ClearButton';
import { SelectIcon } from './SelectIcon';

/**
 * You have the option to provide a getOptionLabel function.
 * This function takes in input the select field's value and allows you to customize the displayed label.
 */
type ConditionalSelectProps =
  | {
      options: string[];
      getOptionLabel?: (value: string) => string;
      children?: never;
    }
  | {
      options?: never;
      getOptionLabel?: never;
      children: ReactNode;
    };

type Props = Omit<TextFieldProps, 'select'> &
  AccessibilityProps &
  ConditionalSelectProps & { clearable?: boolean };

export const WbSelect = forwardRef(
  (
    {
      clearable = false,
      getOptionLabel,
      InputProps,
      SelectProps,
      id,
      ariaLabel,
      label,
      ...props
    }: Props,
    ref?: Ref<any>,
  ) => {
    const accessibilityProps =
      id && label
        ? { id, label }
        : { id, ariaLabel: ariaLabel ?? 'select field' };

    return (
      <WbTextField
        {...props}
        {...accessibilityProps}
        ref={ref}
        value={props.value ?? ''}
        onChange={e => {
          if (props.onChange) props.onChange(e);
        }}
        select
        SelectProps={{
          IconComponent: SelectIcon,
          ...SelectProps,
        }}
        InputProps={{
          endAdornment: clearable && !props.disabled && (
            <ClearButton
              style={{
                position: 'absolute',
                right: '45px',
              }}
              onClear={e => {
                const event = e as unknown as ChangeEvent<
                  HTMLInputElement | HTMLTextAreaElement
                >;
                if (props.onChange) props.onChange(event);
              }}
            />
          ),
          ...InputProps,
        }}
      >
        {props?.options?.map((option, i) => (
          <MenuItem key={i} value={option}>
            {getOptionLabel?.(option) || option}
          </MenuItem>
        ))}

        {props?.children}
      </WbTextField>
    );
  },
);
